import { useEffect, useRef } from 'react';

/**
 * useClickOutside
 *
 * Invokes `handler` when a pointer event occurs outside the element referenced
 * by `ref`. The listener is attached once for the lifetime of the component;
 * the latest `handler` is always called via a ref, so callers may pass an
 * inline function without re-binding the listener on every render.
 *
 * @param {React.RefObject<HTMLElement>} ref - Ref to the "inside" element.
 * @param {(event: Event) => void} handler   - Called on an outside interaction.
 * @param {string} [eventType='mousedown']   - DOM event to listen for.
 *
 * @example
 *   const ref = useRef(null);
 *   useClickOutside(ref, () => setOpen(false));
 */
const useClickOutside = (ref, handler, eventType = 'mousedown') => {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    const listener = (event) => {
      // Ignore clicks inside the referenced element (or if it's not mounted).
      if (!ref.current || ref.current.contains(event.target)) return;
      handlerRef.current(event);
    };
    document.addEventListener(eventType, listener);
    return () => document.removeEventListener(eventType, listener);
  }, [ref, eventType]);
};

export default useClickOutside;
